import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "../../lib/utils";

// Gap between the trigger and the card, in CSS pixels. A number rather than a
// class: the card is positioned by measurement, so the offset has to be a value
// the same arithmetic can use.
const OFFSET_PX = 4;

/**
 * The look of a popover card: the menu `PopoverCard` hangs off a trigger, and
 * the one `LongPressMenu` opens where a finger rests. One definition, so the
 * two menus cannot drift apart.
 */
export const POPOVER_CARD_CLASS =
  "z-9999 flex flex-col overflow-hidden rounded-2xl border border-ds-black/10 bg-white py-1 shadow-popover";

/** Where the card hangs relative to its trigger. */
type PopoverPlacement = {
  vertical: { side: "below"; top: number } | { side: "above"; bottom: number };
  horizontal: { edge: "right"; right: number } | { edge: "left"; left: number };
};

/**
 * The measuring and keyboard half of a popover: give it the open state and it
 * gives back the refs to hang on the trigger and the card, plus the measured
 * placement `PopoverCard` paints with.
 *
 * The card is portalled and `fixed` because the surfaces that open one CLIP: the
 * JuisciGPT history list scrolls inside a transformed drawer, so an in-flow
 * absolute card would be cut off or dragged along. That is also why the position
 * is measured rather than declared, and re-measured on any ancestor scroll.
 *
 * Keyboard behaviour comes with it, because a card of `role="menuitem"` buttons
 * promises it: focus lands on the first item, the arrows walk the list and wrap,
 * and Escape closes and returns focus to the trigger it came from.
 *
 * @param isOpen whether the card is currently rendered.
 * @param onDismiss called when Escape closes the card.
 * @param edge the trigger's edge the card lines up with, when it fits there.
 */
export function usePopoverAnchor({
  isOpen,
  onDismiss,
  edge,
}: {
  isOpen: boolean;
  onDismiss: () => void;
  edge: "left" | "right";
}) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState<PopoverPlacement | null>(null);
  // Read by the keyboard effect, which must not be torn down and rebuilt every
  // time the caller passes a fresh closure.
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;

  const dismiss = useCallback(() => {
    dismissRef.current();
  }, []);

  // Layout effect: the card first renders unplaced and transparent, is measured
  // here, and the placed re-render lands before the browser paints either. A
  // placement computed on guessed dimensions would paint first and then jump.
  useLayoutEffect(() => {
    if (!isOpen) return;

    function updatePlacement(): void {
      const trigger = triggerRef.current;
      if (trigger === null) return;
      const rect = trigger.getBoundingClientRect();
      // `clientWidth`, not `innerWidth`: a fixed element resolves `right`
      // against the initial containing block, which excludes a classic
      // scrollbar. `innerWidth` includes it, and the card would sit that far off
      // the trigger it is supposed to hang from.
      const viewport = document.documentElement.clientWidth;
      const right = viewport - rect.right;
      // Flip up when the card would not fit below — the last row of a scrolled
      // list otherwise puts the bottom item under the fold. When NEITHER side
      // fits (a viewport shorter than the card) take the roomier one rather than
      // flipping on principle: hanging off the top is no better than the bottom.
      const roomBelow = window.innerHeight - rect.bottom;
      const card = cardRef.current;
      if (card === null)
        throw new Error("should not happen: an open popover has no card");
      const { width, height } = card.getBoundingClientRect();
      const openBelow =
        roomBelow >= height + OFFSET_PX || roomBelow >= rect.top;
      setPlacement({
        vertical: openBelow
          ? { side: "below", top: rect.bottom + OFFSET_PX }
          : {
              side: "above",
              bottom: window.innerHeight - rect.top + OFFSET_PX,
            },
        // Hang from the asked edge of the trigger, unless the card would then
        // cross the edge of the screen — a narrow trigger near it, like the
        // collapsed sidebar's profile button — and hang from the other one then.
        horizontal:
          (edge === "left" && rect.left + width <= viewport) ||
          rect.right - width < 0
            ? { edge: "left", left: rect.left }
            : { edge: "right", right },
      });
    }

    updatePlacement();
    // `true`: any ancestor scroll moves the trigger, and the drawer list is one.
    window.addEventListener("scroll", updatePlacement, true);
    window.addEventListener("resize", updatePlacement);
    return () => {
      window.removeEventListener("scroll", updatePlacement, true);
      window.removeEventListener("resize", updatePlacement);
    };
  }, [isOpen, edge]);

  useEffect(() => {
    if (!isOpen) return;
    const card = cardRef.current;
    if (card === null) return;

    const items = [
      ...card.querySelectorAll<HTMLButtonElement>(
        '[role="menuitem"], [role="menuitemradio"]'
      ),
    ];
    // A menu that picks a value opens on the value in use.
    const checkedItem = card.querySelector<HTMLButtonElement>(
      '[role="menuitemradio"][aria-checked="true"]'
    );
    const firstItem = checkedItem === null ? items[0] : checkedItem;
    if (firstItem !== undefined) firstItem.focus();

    function handleKeyDown(event: KeyboardEvent): void {
      // Tab leaves a menu the way Escape does: the card is portalled to the
      // end of the document, so letting Tab through would move the focus to
      // whatever follows it there, not to the control after the trigger.
      // Escape is marked handled too: a later listener (the composer's
      // Escape-to-stop) runs after this close has already removed the menu.
      if (event.key === "Escape" || event.key === "Tab") {
        event.preventDefault();
        dismissRef.current();
        const trigger = triggerRef.current;
        if (trigger !== null) trigger.focus();
        return;
      }
      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
      if (items.length === 0) return;
      event.preventDefault();
      const current = items.findIndex(
        (item) => item === document.activeElement
      );
      const step = event.key === "ArrowDown" ? 1 : -1;
      // `+ items.length` so stepping up from the first wraps to the last rather
      // than landing on -1. A list nothing is focused in starts at the first.
      const next =
        current === -1 ? 0 : (current + step + items.length) % items.length;
      const target = items[next];
      if (target === undefined)
        throw new Error(
          `Invariant: popover index ${String(next)} out of range`
        );
      target.focus();
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  return { triggerRef, cardRef, placement, dismiss };
}

/**
 * A trigger that opens one menu card: its open state, owned here with the
 * anchor so a surface rendering the menu keeps no state of its own.
 */
export function usePopoverMenu(edge: "left" | "right") {
  const [isOpen, setIsOpen] = useState(false);
  const anchor = usePopoverAnchor({
    isOpen,
    onDismiss: () => {
      setIsOpen(false);
    },
    edge,
  });
  return {
    isOpen,
    toggle: () => {
      setIsOpen(!isOpen);
    },
    close: () => {
      setIsOpen(false);
    },
    anchor,
  };
}

/**
 * What `usePopoverAnchor` hands to `PopoverCard`, inferred rather than restated:
 * the ref types React's own `useRef` produces are the ones the `ref` prop
 * accepts, and writing them out by hand gets that wrong.
 */
export type PopoverAnchor = ReturnType<typeof usePopoverAnchor>;

/**
 * The painted half: a small card portalled to the body over a transparent
 * full-screen backdrop that takes the outside click. Before the anchor has
 * measured it renders transparent and unplaced, for that measurement only: the
 * placed render lands before the first paint.
 */
export function PopoverCard({
  anchor,
  role,
  className,
  children,
}: {
  anchor: PopoverAnchor;
  /** `"menu"` for a list of actions; omit for plain content. */
  role: "menu" | null;
  className: string | null;
  children: React.ReactNode;
}): React.ReactElement {
  const { placement } = anchor;

  return createPortal(
    <>
      <div className="fixed inset-0 z-9998" onClick={anchor.dismiss} />
      <div
        ref={anchor.cardRef}
        role={role === null ? undefined : role}
        style={
          placement === null
            ? // Only the measuring pass: placed before the first paint.
              { position: "fixed", top: 0, left: 0, opacity: 0 }
            : {
                position: "fixed",
                ...(placement.vertical.side === "below"
                  ? { top: placement.vertical.top }
                  : { bottom: placement.vertical.bottom }),
                ...(placement.horizontal.edge === "right"
                  ? { right: placement.horizontal.right }
                  : { left: placement.horizontal.left }),
              }
        }
        className={cn(POPOVER_CARD_CLASS, className)}
      >
        {children}
      </div>
    </>,
    document.body
  );
}
